import React, { useCallback, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, useAnimation } from 'framer-motion';
import { ThemeContext } from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';
import LiquidIDAddPrompt from './LiquidIDAddPrompt';
import LiquidIDItemDetails from './LiquidIDItemDetails';
import LiquidIDThumbnails from './LiquidIDThumbnails';
import {
    LiquidIDSectionWrapper,
    LiquidIDPosition,
    CreateIDButton,
    EditButtonsGroup,
    CloseSelectionButton,
    LayoutSplitter,
    NavigationBar,
    ExpandedOwnerActionsWrapper,
} from './styles';
import { PathParams } from '@/routes/types';
import {
    reorderLiquidID,
    setDraggingLiquidIDThumbnail,
    setYatPageLiquidID,
    setLiquidIdEditStage,
    updateYatPageEditStates,
    setLiquidIdLoading,
} from '@/store/slices/yat-page';
import { LiquidIDEditStage } from '@/store/slices/yat-page/types';
import { NFTItem } from '@/store/types/nft-manager';

import { ReactComponent as Close } from '@/components/Sections/Profile/icons/close.svg';

import { useAppSelector, useAppDispatch, LID_CACHE } from '@/hooks';
import { useMediaQuery } from '@/providers';

import { publish, useTopic } from '@/components/Sections/Profile/pubSub';
import {
    glRemoveFace,
    glShowUIButtons,
    glLiquidIDDraggingToFaceIndex,
} from '@/components/Sections/Profile/pubSub/constants';

import { Button } from '@/components/Sections/Profile/Buttons';
import { YatShareModule } from '@/components';

import { useHasFreeYat } from '@/helpers/hooks/useHasFreeYat';

import { publishLidChanges } from '@/store/actions/yat-page-item-actions';

interface Props {
    toggleFreeYatModal?(): void;
    editMode?: boolean;
}

const LiquidIDSection = ({ editMode = false, toggleFreeYatModal }: Props) => {
    const dispatch = useAppDispatch();
    const {
        liquidIDItems,
        liquidIDEditStage,
        liquidIDExpandedPreview,
        liquidIDLoading,
        editStates: { editingLiquidID },
    } = useAppSelector((state) => state.yatPage);

    const navigate = useNavigate();
    const theme = useContext(ThemeContext);
    const { tablet } = useMediaQuery();
    const { t } = useTranslation(['yat_page']);
    const initialItemsRef = useRef<(NFTItem | undefined)[]>([]);
    const hasInitialRef = useRef(false);
    const uiControls = useAnimation();
    const hasOnlyFreeYat = useHasFreeYat();

    const { [PathParams.Yat]: yat } = useParams();
    const primaryYat = useAppSelector((state) => state.user?.primaryYat);
    const isCurrentUserProfile = primaryYat == yat;
    const queryClient = useQueryClient();

    useTopic(glShowUIButtons, (value) => {
        // Controls display of thumbnails when transitioning between editor/preview + collapsed
        if (uiControls) {
            const reveal = value && ((liquidIDExpandedPreview && !tablet) || editingLiquidID);
            uiControls.start(reveal ? 'visible' : 'hidden');
        }
    });

    useEffect(() => {
        if (liquidIDLoading) {
            initialItemsRef.current = [];
            hasInitialRef.current = false;
            return;
        }

        if (liquidIDEditStage === LiquidIDEditStage.Selection) {
            if (!hasInitialRef.current) {
                // to handle the cancel
                initialItemsRef.current = liquidIDItems;
                hasInitialRef.current = true;
            }
        } else {
            initialItemsRef.current = [];
            hasInitialRef.current = false;
        }
    }, [liquidIDEditStage, liquidIDItems, liquidIDLoading]);

    const handleCancel = () => {
        const currentItems = liquidIDItems;
        const replacedWith = initialItemsRef.current;

        const filteredInitial = replacedWith.filter((p) => p !== undefined) as NFTItem[];
        const filteredCurrent = currentItems.filter((p) => p !== undefined) as NFTItem[];

        const replacingIds = filteredInitial.map((p) => p.id);
        const currentIds = filteredCurrent.map((p) => p.id);

        const idsToUpdate: string[] = [];

        currentIds.forEach((id) => {
            if (!replacingIds.includes(id)) {
                idsToUpdate.push(id);
            }
        });

        replacingIds.forEach((id) => {
            if (!currentIds.includes(id)) {
                idsToUpdate.push(id);
            }
        });

        dispatch(setYatPageLiquidID({ items: initialItemsRef.current }));
        handleCloseButton();
    };

    const removeFace = useCallback(
        (face: number) => {
            const currentItems = [...liquidIDItems];
            currentItems[face] = undefined;

            dispatch(setYatPageLiquidID({ items: currentItems }));

            publish(glRemoveFace, currentItems);
        },
        [liquidIDItems, dispatch],
    );

    const handleDragStart = (draggingId: string) => {
        dispatch(setDraggingLiquidIDThumbnail(draggingId));
    };

    const handleDrag = (from: number, to: number) => {
        publish(glLiquidIDDraggingToFaceIndex, to);
        dispatch(reorderLiquidID({ from, to }));
    };

    const handleDragEnd = () => {
        publish(glLiquidIDDraggingToFaceIndex, undefined);
        dispatch(setDraggingLiquidIDThumbnail());
    };

    const updateEditStage = (newStage: LiquidIDEditStage) => {
        dispatch(setLiquidIdEditStage(newStage));
        dispatch(updateYatPageEditStates({ editingLiquidID: newStage === LiquidIDEditStage.Selection }));
        navigate({ search: '' });
    };

    const handleCloseButton = () => {
        if (editingLiquidID) return updateEditStage(LiquidIDEditStage.Added);
        navigate({ search: '' });
    };

    const clearLidCache = () => {
        queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey[0] === LID_CACHE });
    };

    const handleSave = () => {
        dispatch(publishLidChanges(clearLidCache));
        updateEditStage(LiquidIDEditStage.Added);
        navigate(`/${primaryYat}?s=1`);
    };

    const goToProfileEdit = async () => {
        if (hasOnlyFreeYat && toggleFreeYatModal) {
            toggleFreeYatModal();
        } else {
            await navigate(`/profile/edit`);
            dispatch(setLiquidIdLoading(true));
            dispatch(updateYatPageEditStates({ editingLiquidID: true }));
            dispatch(setLiquidIdEditStage(LiquidIDEditStage.Selection));
        }
    };

    const sharedTransitionProps = {
        duration: theme.transitions.speeds.fast,
        ease: theme.transitions.eases.on,
    };

    const liquidIDFilledFaces = liquidIDItems.filter((item) => item !== undefined && item !== null).length;

    return (
        <AnimatePresence>
            {(editMode && editingLiquidID) || liquidIDExpandedPreview ? (
                <LayoutSplitter
                    key="layout-splitter"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <LiquidIDSectionWrapper
                        key="liquid-id-section-wrapper"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={sharedTransitionProps}
                    >
                        <LiquidIDPosition editMode={editMode} />
                        <EditButtonsGroup
                            key="thumbnailsWrapper"
                            initial={'hidden'}
                            variants={{
                                hidden: {
                                    opacity: 0,
                                },
                                visible: {
                                    opacity: 1,
                                },
                            }}
                            animate={uiControls}
                            transition={{
                                duration: theme.transitions.speeds.normal,
                                ease: theme.transitions.eases.cubic,
                            }}
                        >
                            <LiquidIDThumbnails
                                removeFace={removeFace}
                                onDragStart={handleDragStart}
                                onDrag={handleDrag}
                                onDragEnd={handleDragEnd}
                            />
                        </EditButtonsGroup>
                    </LiquidIDSectionWrapper>
                    {editingLiquidID && <LiquidIDAddPrompt hidden={liquidIDLoading || liquidIDFilledFaces !== 0} />}
                    {!liquidIDLoading && (!editingLiquidID || liquidIDFilledFaces !== 0) && (
                        <LiquidIDItemDetails expanded />
                    )}
                </LayoutSplitter>
            ) : null}
            <NavigationBar
                $editor={editingLiquidID}
                $userLIDPreview={liquidIDExpandedPreview && !editingLiquidID && isCurrentUserProfile}
            >
                {/*!editMode && !liquidIDExpandedPreview && showTNY && <ShareButton />*/}

                {liquidIDExpandedPreview && (
                    <>
                        {!editingLiquidID && isCurrentUserProfile && (
                            <ExpandedOwnerActionsWrapper>
                                <YatShareModule
                                    emojiId={yat as string}
                                    soloButton={true}
                                    directionUp={false}
                                    directionRight={false}
                                    buttonSize="medium"
                                    buttonVariant="yat"
                                    buttonColor="primary"
                                />
                                <Button variant="solid" color="translucent" onClick={goToProfileEdit}>
                                    {t('edit_lid')}
                                </Button>
                            </ExpandedOwnerActionsWrapper>
                        )}
                        <CloseSelectionButton
                            key="liquid-id-close-button"
                            onClick={handleCloseButton}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={sharedTransitionProps}
                        >
                            <Close />
                        </CloseSelectionButton>
                    </>
                )}

                {editingLiquidID && (
                    <>
                        <Button variant="solid" color="translucent" onClick={handleCancel}>
                            {t('edit_profile_cancel')}
                        </Button>
                        <CreateIDButton key="edit-create" onClick={handleSave}>
                            {t('liquid_id_create_liquid_id')}
                        </CreateIDButton>
                    </>
                )}
            </NavigationBar>
        </AnimatePresence>
    );
};

export default LiquidIDSection;
